/**
 * Forum Payment Thank-You — Branded post-checkout confirmation page.
 *
 * Stripe redirects to /events/invest-uae-forum-2026?payment=success&session_id=...
 * — InvestUAEForum2026 already polls and shows a banner. This separate /thank-you
 * route gives an editorial, full-page confirmation experience (better for
 * marketing screenshots, social proof posts, and word-of-mouth).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Calendar, Mail, Loader2, ArrowRight, Download } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.tyk{background:#FAF7F0;min-height:100vh;color:#0D1B2A;font-family:Inter,system-ui,sans-serif;}
.tyk-shell{max-width:920px;margin:0 auto;padding:64px 32px 96px;}
@media(max-width:680px){.tyk-shell{padding:36px 22px 64px;}}

/* HERO */
.tyk-hero{background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 60%,#0D1B2A 100%);color:#FAF7F0;padding:64px 56px 60px;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
.tyk-hero::before{content:'';position:absolute;top:-40px;right:-40px;width:240px;height:240px;background:radial-gradient(circle,rgba(200,168,75,.18) 0%,transparent 70%);}
.tyk-check{display:inline-flex;align-items:center;justify-content:center;width:64px;height:64px;border-radius:50%;background:rgba(200,168,75,.2);border:2px solid #C8A84B;color:#C8A84B;margin-bottom:24px;}
.tyk-eyebrow{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:16px;display:flex;align-items:center;gap:14px;}
.tyk-eyebrow::before{content:'';width:32px;height:1px;background:#C8A84B;}
.tyk-h1{font-family:'Cormorant Display',serif;font-size:clamp(40px,5.5vw,64px);font-weight:700;line-height:1.02;letter-spacing:-.02em;margin:0 0 18px;}
.tyk-h1 em{font-style:italic;color:#C8A84B;}
.tyk-sub{font-size:17px;line-height:1.6;color:rgba(250,247,240,.78);max-width:620px;margin:0;font-weight:300;}

/* RECEIPT */
.tyk-receipt{background:#fff;border:1px solid #E8DFC9;border-left:3px solid #C8A84B;padding:38px 44px;margin-top:-2px;}
.tyk-rec-l{font-size:11px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#7A8190;margin-bottom:14px;}
.tyk-rec-row{display:flex;justify-content:space-between;align-items:center;padding:13px 0;border-bottom:1px solid #F0EBDD;font-size:14px;}
.tyk-rec-row:last-child{border:0;}
.tyk-rec-k{color:#7A8190;letter-spacing:.04em;font-size:12px;text-transform:uppercase;font-weight:600;}
.tyk-rec-v{color:#0D1B2A;font-weight:600;text-align:right;}
.tyk-rec-amount{font-family:'Cormorant Display',serif;font-size:28px;font-weight:700;color:#0D1B2A;letter-spacing:-.005em;}
.tyk-rec-ref{font-family:'JetBrains Mono','Courier New',monospace;font-size:13px;color:#0D1B2A;letter-spacing:.04em;}

/* NEXT STEPS */
.tyk-next{margin-top:48px;}
.tyk-next-h{font-family:'Cormorant Display',serif;font-size:36px;font-weight:700;line-height:1.1;letter-spacing:-.012em;margin:0 0 28px;color:#0D1B2A;}
.tyk-next-h em{font-style:italic;color:#C8A84B;}
.tyk-step{display:flex;gap:24px;align-items:flex-start;padding:20px 0;border-top:1px solid #E8DFC9;}
.tyk-step:first-of-type{border-top:1px solid #E8DFC9;}
.tyk-step-num{flex-shrink:0;width:38px;height:38px;display:flex;align-items:center;justify-content:center;border:1px solid #C8A84B;color:#C8A84B;font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;font-style:italic;border-radius:50%;}
.tyk-step-t{font-family:'Cormorant Display',serif;font-size:21px;font-weight:700;color:#0D1B2A;line-height:1.2;margin:0 0 4px;letter-spacing:-.005em;}
.tyk-step-d{font-size:13px;color:#3D4A5C;line-height:1.6;margin:0;}
.tyk-step-tag{display:inline-block;font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;background:rgba(200,168,75,.1);padding:3px 10px;margin-top:8px;}

/* CTA */
.tyk-cta-row{margin-top:48px;display:flex;gap:14px;flex-wrap:wrap;justify-content:center;padding:32px;background:#FAF7F0;border:1px solid #E8DFC9;}
.tyk-cta-pri{background:#0D1B2A;color:#C8A84B;padding:14px 28px;text-decoration:none;font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;border:1px solid #0D1B2A;display:inline-flex;align-items:center;gap:8px;transition:all .2s;}
.tyk-cta-pri:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.tyk-cta-sec{background:transparent;color:#0D1B2A;padding:14px 28px;text-decoration:none;font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;border:1px solid rgba(13,27,42,.25);display:inline-flex;align-items:center;gap:8px;transition:all .2s;}
.tyk-cta-sec:hover{border-color:#C8A84B;color:#C8A84B;}

/* SUPPORT FOOTER */
.tyk-support{margin-top:32px;padding:24px 32px;background:rgba(13,27,42,.04);border-left:3px solid rgba(200,168,75,.4);text-align:center;font-size:12px;color:#3D4A5C;line-height:1.6;font-style:italic;font-family:'Cormorant Display',serif;font-size:14px;}
.tyk-support a{color:#C8A84B;text-decoration:none;font-style:normal;font-family:Inter,sans-serif;font-weight:600;font-size:13px;}

/* LOADER / ERROR */
.tyk-loading{padding:80px 40px;text-align:center;color:#3D4A5C;font-size:14px;}
.tyk-fail{padding:48px 40px;background:#fff;border:1px solid #E8DFC9;border-left:3px solid #ad6240;text-align:center;}
.tyk-fail h2{font-family:'Cormorant Display',serif;font-size:32px;color:#0D1B2A;margin:0 0 12px;font-weight:700;}
.tyk-fail p{margin:0 0 20px;font-size:14px;color:#3D4A5C;line-height:1.6;}
`;

const NEXT_STEPS = [
  { num: 'i', tag: 'Now', title: 'Receipt + Calendar Invite', desc: 'A branded receipt email is on its way to your inbox, with a .ics calendar invite attached so the forum auto-syncs to your calendar with the correct time zone.' },
  { num: 'ii', tag: '5 days before', title: 'Forum Prep Briefing', desc: 'You\'ll receive a curated pre-read with the full agenda, speaker bios, and the 3 questions worth asking each panel — so you arrive informed.' },
  { num: 'iii', tag: '24 hours before', title: 'Your Personal Zoom Link', desc: 'A unique, named Zoom join link arrives in your inbox. Save it. Don\'t share it. We use room-control to keep the conversation institutional-grade.' },
  { num: 'iv', tag: 'Live · 31 May', title: 'The Forum (2 hours)', desc: '12 voices. Sovereign capital, governance survivability, AI ownership, UAE real estate, and the cross-border architecture making this the most consequential investment geography of the next decade.' },
  { num: 'v', tag: 'Within 48 hours', title: 'Recording + UAE Investor Briefing PDF', desc: 'A high-definition forum recording lands in your inbox, alongside the proprietary 2026 UAE Investor Briefing — exclusive to attendees.' },
];

export default function ForumThankYou() {
  const navigate = useNavigate();
  useSEO({
    title: 'Payment Confirmed · Invest UAE Forum 2026 · SKI AXIS',
    description: 'Your seat is confirmed. The Invest UAE Forum 2026 lands Monday, 31 May.',
    noindex: true,
  });

  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const sessionId = params.get('session_id');
  const paymentIntentId = params.get('payment_intent');  // Express Checkout flow
  const [state, setState] = useState({ loading: true, paid: false, status: null, error: null });

  useEffect(() => {
    if (!sessionId && !paymentIntentId) {
      setState({ loading: false, paid: false, status: null, error: 'No session id provided.' });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const url = paymentIntentId
          ? `${API}/payments/payment-intent/status/${paymentIntentId}`
          : `${API}/payments/status/${sessionId}`;
        const { data } = await axios.get(url);
        if (cancelled) return;
        if (data?.paid) {
          setState({ loading: false, paid: true, status: data, error: null });
          return;
        }
        if (data?.status === 'expired' || data?.status === 'canceled') {
          setState({ loading: false, paid: false, status: data, error: 'Payment session expired. Please try again from the registration form.' });
          return;
        }
        if (attempts < 8) {
          setTimeout(poll, 2000);
        } else {
          setState({ loading: false, paid: false, status: data, error: "We couldn't confirm payment in time. If your card was charged, you'll still receive a confirmation email shortly." });
        }
      } catch {
        if (attempts < 8) {
          setTimeout(poll, 2500);
        } else if (!cancelled) {
          setState({ loading: false, paid: false, status: null, error: "Couldn't reach payment service. Please contact accelerator@skiasiapacific.com if your card was charged." });
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, paymentIntentId]);

  if (state.loading) {
    return (
      <div className="tyk">
        <style>{STYLES}</style>
        <div className="tyk-shell tyk-loading">
          <Loader2 className="inline h-6 w-6 animate-spin mr-3 text-amber-600" />
          Confirming your payment with Stripe…
        </div>
      </div>
    );
  }

  if (!state.paid) {
    return (
      <div className="tyk">
        <style>{STYLES}</style>
        <div className="tyk-shell">
          <div className="tyk-fail">
            <h2>Payment status unclear</h2>
            <p>{state.error || 'We couldn\'t confirm your payment. If your card was charged, you\'ll still receive a confirmation email — and your seat is reserved.'}</p>
            <a href="/events/invest-uae-forum-2026" className="tyk-cta-pri">← Back to Forum</a>
          </div>
        </div>
      </div>
    );
  }

  const ref = (paymentIntentId || sessionId || '').slice(-8).toUpperCase();
  const amount = state.status?.amount_total || 149;
  const currency = (state.status?.currency || 'AED').toUpperCase();
  const paidStr = new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) + ' UTC';

  return (
    <div className="tyk" data-testid="forum-thankyou-page">
      <style>{STYLES}</style>
      <div className="tyk-shell">

        {/* HERO */}
        <div className="tyk-hero">
          <div className="tyk-check"><CheckCircle2 className="h-7 w-7" /></div>
          <div className="tyk-eyebrow">Payment Confirmed · Receipt #{ref}</div>
          <h1 className="tyk-h1">Welcome to <em>Invest UAE 2026</em>.</h1>
          <div style={{ fontFamily: "'Cormorant Display',serif", fontSize: 'clamp(20px,2.4vw,26px)', color: '#C8A84B', fontStyle: 'italic', fontWeight: 600, margin: '0 0 22px', letterSpacing: '-.005em' }}>
            Your seat is secured.
          </div>
          <p className="tyk-sub">Thank you, {((state.status?.full_name || '').split(' ')[0]) || 'Operator'}. The forum lands Monday, 31 May — you're in the room. Below is your receipt, the timeline, and the calendar invite arriving in your inbox now.</p>
        </div>

        {/* RECEIPT */}
        <div className="tyk-receipt">
          <div className="tyk-rec-l">Receipt Details</div>
          <div className="tyk-rec-row">
            <span className="tyk-rec-k">Amount Paid</span>
            <span className="tyk-rec-amount">{currency} {Number(amount).toFixed(2)}</span>
          </div>
          <div className="tyk-rec-row">
            <span className="tyk-rec-k">Reference</span>
            <span className="tyk-rec-ref">#{ref}</span>
          </div>
          <div className="tyk-rec-row">
            <span className="tyk-rec-k">Paid At</span>
            <span className="tyk-rec-v">{paidStr}</span>
          </div>
          <div className="tyk-rec-row">
            <span className="tyk-rec-k">Method</span>
            <span className="tyk-rec-v">Stripe · Card / Apple Pay / Google Pay</span>
          </div>
          <div className="tyk-rec-row">
            <span className="tyk-rec-k">Event</span>
            <span className="tyk-rec-v">Invest UAE Forum 2026</span>
          </div>
          <div className="tyk-rec-row">
            <span className="tyk-rec-k">Date</span>
            <span className="tyk-rec-v">Sunday, 31 May 2026 · 6:00 PM BST</span>
          </div>
        </div>

        {/* NEXT STEPS */}
        <div className="tyk-next">
          <h2 className="tyk-next-h">What <em>happens next</em></h2>
          {NEXT_STEPS.map((step, i) => (
            <div key={i} className="tyk-step">
              <div className="tyk-step-num">{step.num}</div>
              <div style={{ flex: 1 }}>
                <h3 className="tyk-step-t">{step.title}</h3>
                <p className="tyk-step-d">{step.desc}</p>
                <div className="tyk-step-tag">{step.tag}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="tyk-cta-row">
          <Link to="/events/invest-uae-forum-2026" className="tyk-cta-pri" data-testid="tyk-cta-forum">
            <ArrowRight className="h-3.5 w-3.5" /> Forum Page
          </Link>
          <Link to="/blog" className="tyk-cta-sec" data-testid="tyk-cta-blog">
            <Calendar className="h-3.5 w-3.5" /> Pre-Reads &amp; Insights
          </Link>
          <a
            href="https://www.linkedin.com/sharing/share-offsite/?url=https://skiaxis.com/events/invest-uae-forum-2026"
            target="_blank"
            rel="noopener noreferrer"
            className="tyk-cta-sec"
            data-testid="tyk-cta-share"
          >
            <Mail className="h-3.5 w-3.5" /> Share on LinkedIn
          </a>
        </div>

        {/* SUPPORT */}
        <div className="tyk-support">
          Questions about your booking? Email <a href={`mailto:accelerator@skiasiapacific.com?subject=Forum%20Booking%20${ref}`}>accelerator@skiasiapacific.com</a> with reference <strong>#{ref}</strong>.
        </div>
      </div>
    </div>
  );
}
